import { useState, type FormEvent } from 'react';
import { Link, Navigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api/client.js';
import type { CatalogApp, CatalogRole } from '../api/types.js';
import { useApp, useDocumentTitle, useMe } from '../app/context.js';
import { IconChevronLeft } from '../components/icons.js';
import { useToast } from '../components/toast.js';
import { Sheet } from '../components/ui.js';

/** Tool-Katalog: Name, Beschreibung, Adresse und Rollen je Tool. Grundlage für Kacheln und Rollenvergabe. */
export function AdminCatalogPage() {
  useDocumentTitle('Tool-Katalog');
  const { api } = useApp();
  const me = useMe().data!;
  const catalog = useQuery({ queryKey: ['admin', 'catalog'], queryFn: () => api.catalog(), enabled: me.platformAdmin });
  const [editing, setEditing] = useState<CatalogApp | 'new' | null>(null);

  if (!me.platformAdmin) return <Navigate to="/" replace />;

  return (
    <main className="page">
      <header className="subheader">
        <Link to="/admin" className="icon-btn icon-btn--bare" aria-label="Zurück">
          <IconChevronLeft size={22} />
        </Link>
        <div className="page__head--stack" style={{ flex: 1 }}>
          <span className="eyebrow">Administration</span>
          <h1 className="page__title">Tool-Katalog</h1>
        </div>
        <button className="btn btn--primary" onClick={() => setEditing('new')}>+ Tool</button>
      </header>

      <section className="list">
        {catalog.data?.apps.map((a) => (
          <article key={a.appId} className="list__item">
            <div className="list__main">
              <strong>
                {a.name} <span className="mono muted">({a.appId})</span>
              </strong>
              <span className="muted">{a.description}</span>
              <span className="mono muted" style={{ fontSize: 12 }}>{a.url}</span>
              <span className="badges">{a.roles.map((r) => <span key={r.id} className="badge badge--neutral">{r.label}</span>)}</span>
            </div>
            <button className="btn btn--ghost btn--sm" onClick={() => setEditing(a)}>Bearbeiten</button>
          </article>
        ))}
      </section>
      <p className="note">Neue Tools brauchen zusätzlich einen App-Client im User Pool (infra/cdk.json → toolClients) und ein Plattform-Deployment.</p>

      {editing && <CatalogSheet app={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </main>
  );
}

function CatalogSheet({ app, onClose }: { app?: CatalogApp; onClose: () => void }) {
  const { api } = useApp();
  const qc = useQueryClient();
  const toast = useToast();
  const [appId, setAppId] = useState(app?.appId ?? '');
  const [name, setName] = useState(app?.name ?? '');
  const [description, setDescription] = useState(app?.description ?? '');
  const [url, setUrl] = useState(app?.url ?? 'https://');
  const [order, setOrder] = useState(String(app?.order ?? 100));
  const [roles, setRoles] = useState<CatalogRole[]>(app?.roles ?? [{ id: '', label: '' }]);

  const save = useMutation({
    mutationFn: () =>
      api.putCatalogApp({ appId, name: name.trim(), description: description.trim(), url: url.trim(), order: Number(order) || 0, roles: roles.filter((r) => r.id && r.label) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['admin', 'catalog'] });
      await qc.invalidateQueries({ queryKey: ['me'] });
      toast('Tool gespeichert.');
      onClose();
    },
  });
  const valid = /^[a-z][a-z0-9-]{1,40}$/.test(appId) && name.trim().length >= 2 && /^https:\/\/\S+$/.test(url.trim()) && roles.some((r) => r.id && r.label);
  const setRole = (i: number, patch: Partial<CatalogRole>) => setRoles((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  function submit(e: FormEvent) {
    e.preventDefault();
    if (valid) save.mutate();
  }

  return (
    <Sheet title={app ? app.name : 'Tool eintragen'} onClose={onClose}>
      <form onSubmit={submit} className="form">
        <div className="field">
          <label className="field__label" htmlFor="c-id">Tool-ID</label>
          <input id="c-id" className="input mono" value={appId} disabled={Boolean(app)} onChange={(e) => setAppId(e.target.value.toLowerCase())} placeholder="z. B. terminplaner" />
          <span className="field__hint">Kleinbuchstaben, Ziffern, Bindestrich – identisch mit custom:apps und den Rollen-Präfixen.</span>
        </div>
        <div className="field">
          <label className="field__label" htmlFor="c-name">Name</label>
          <input id="c-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="c-desc">Beschreibung (Kachel)</label>
          <input id="c-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="c-url">Adresse</label>
          <input id="c-url" className="input mono" value={url} onChange={(e) => setUrl(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="c-order">Reihenfolge</label>
          <input id="c-order" className="input" inputMode="numeric" value={order} onChange={(e) => setOrder(e.target.value)} />
        </div>
        <fieldset className="role-editor">
          <legend className="field__label">Rollen</legend>
          {roles.map((r, i) => (
            <div key={i} className="grid-2">
              <input className="input mono" aria-label="Rollen-ID" placeholder="id, z. B. owner" value={r.id} onChange={(e) => setRole(i, { id: e.target.value })} />
              <input className="input" aria-label="Bezeichnung" placeholder="Bezeichnung" value={r.label} onChange={(e) => setRole(i, { label: e.target.value })} />
            </div>
          ))}
          <button type="button" className="link-btn" onClick={() => setRoles((rs) => [...rs, { id: '', label: '' }])}>+ Rolle</button>
        </fieldset>
        {save.error && <div className="alert alert--error">{(save.error as ApiError).message}</div>}
        <div className="grid-2">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn btn--primary" disabled={!valid || save.isPending}>Speichern</button>
        </div>
      </form>
    </Sheet>
  );
}
